import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CompanyExportField, ExportDataType, ProfileExportField } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';
import type { InfraFailure } from '@/utils/infra-error';

import type { DeactivatedExportScope } from '../domain/build-export-request';
import {
  type ExportFormat,
  setGroupColumns,
  toggleColumn,
  toggleDataType,
} from '../domain/export-plan.model';
import { initialState } from './profile-export.state';

/** Which column list a column action targets. */
export type ColumnSection = 'profiles' | 'companies';

/**
 * Profile-export slice. Reducers are pure state transitions only; the export
 * call, the delay between CSV downloads and the download triggers all live in
 * the saga, which dispatches the `exportStarted/Succeeded/Failed` transitions
 * below as it progresses.
 */
const slice = createSlice({
  name: 'profileExport',
  initialState,
  reducers: {
    /**
     * Reset the whole wizard for the mounting route. Mandatory on page mount:
     * one slice serves four routes, so a finished export must not leak into
     * the next visit.
     */
    formInitialized: (
      state,
      action: PayloadAction<{
        listId: string | null;
        filters: FilterGroup;
        profileCount: number;
        baseName: string;
        deactivated?: DeactivatedExportScope | null;
        licenceId?: string | null;
      }>,
    ) => ({
      ...initialState,
      ...action.payload,
      // Explicit, because the payload's keys are optional: spreading an absent key
      // would leave the previous route's scope in place on the next visit.
      deactivated: action.payload.deactivated ?? null,
      licenceId: action.payload.licenceId ?? null,
    }),

    // ── Choose Format ──
    formatChanged: (state, action: PayloadAction<ExportFormat>) => {
      state.format = action.payload;
    },

    // ── Choose Data ──
    /** Guarded by the domain: at least one data type always stays selected. */
    dataTypeToggled: (state, action: PayloadAction<ExportDataType>) => {
      state.dataTypes = toggleDataType(state.dataTypes, action.payload);
    },

    // ── Choose Columns ──
    columnToggled: (
      state,
      action: PayloadAction<
        | { section: 'profiles'; field: ProfileExportField }
        | { section: 'companies'; field: CompanyExportField }
      >,
    ) => {
      if (action.payload.section === 'profiles') {
        state.profileColumns = toggleColumn(state.profileColumns, action.payload.field);
      } else {
        state.companyColumns = toggleColumn(state.companyColumns, action.payload.field);
      }
    },
    columnGroupToggled: (
      state,
      action: PayloadAction<
        | { section: 'profiles'; fields: ProfileExportField[]; checked: boolean }
        | { section: 'companies'; fields: CompanyExportField[]; checked: boolean }
      >,
    ) => {
      if (action.payload.section === 'profiles') {
        state.profileColumns = setGroupColumns(
          state.profileColumns,
          action.payload.fields,
          action.payload.checked,
        );
      } else {
        state.companyColumns = setGroupColumns(
          state.companyColumns,
          action.payload.fields,
          action.payload.checked,
        );
      }
    },
    /** "All" / "None" shortcuts of a column picker section. */
    allColumnsToggled: (
      state,
      action: PayloadAction<{ section: ColumnSection; selected: boolean }>,
    ) => {
      if (action.payload.section === 'profiles') {
        state.profileColumns = action.payload.selected ? initialState.profileColumns : [];
      } else {
        state.companyColumns = action.payload.selected ? initialState.companyColumns : [];
      }
    },

    // ── Navigation ──
    stepChanged: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },

    // ── Export lifecycle (dispatched by the saga) ──
    exportStarted: (state) => {
      state.exporting = true;
      state.error = null;
    },
    exportSucceeded: (state) => {
      state.exporting = false;
      state.done = true;
    },
    exportFailed: (state, action: PayloadAction<InfraFailure>) => {
      state.exporting = false;
      state.error = action.payload;
    },

    /** "New Export" on the done screen: back to step 0, selections kept. */
    newExportStarted: (state) => {
      state.done = false;
      state.step = 0;
    },
    errorCleared: (state) => {
      state.error = null;
    },
  },
});

export const profileExportActions = slice.actions;
export const profileExportReducer = slice.reducer;
